import { useEffect, useRef, type ReactNode } from 'react'
import { ArrowLeft, X } from 'lucide-react'
import { useIsShownScreen } from '../adminTrail.js'

interface ModalProps {
  /** Small coral label above the title, e.g. "Preview". */
  eyebrow: string
  title: string
  subtitle?: ReactNode
  /** Scrolls when it is taller or wider than the modal. */
  children: ReactNode
  footer: ReactNode
  /** False while something is in flight that closing would orphan. */
  canClose?: boolean
  /** What this dialog was opened from, when that is another dialog: closing goes back to it, so the header says so. */
  backLabel?: string
  onClose: () => void
}

/** The dialog shell of both apps: dimmed backdrop, header with a close X at its top right, scrolling body, footer; Escape, the X or a backdrop click closes it. */
export function Modal({ eyebrow, title, subtitle, children, footer, canClose = true, backLabel, onClose }: ModalProps) {
  // A dialog on a screen kept for Back is still mounted; Escape there must not close it.
  const isShown = useIsShownScreen()
  // Where the press began: a click counts as a backdrop click only when it both began and ended there, so a text
  // selection dragged out of a field, or a password manager's or autofill menu over the dialog, never closes it.
  const pressStartedOnBackdrop = useRef(false)
  useEffect(() => {
    if (!isShown) {
      return
    }
    function closeOnEscape(event: KeyboardEvent): void {
      // An Escape that dismissed something else first (an autofill menu, a picker) is not meant for the dialog.
      if (event.key === 'Escape' && canClose && !event.defaultPrevented) {
        onClose()
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [canClose, onClose, isShown])

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4"
      onMouseDown={(event) => {
        pressStartedOnBackdrop.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        const isBackdropClick = pressStartedOnBackdrop.current && event.target === event.currentTarget
        pressStartedOnBackdrop.current = false
        if (isBackdropClick && canClose) {
          onClose()
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-white w-full max-w-4xl max-h-[85vh] flex flex-col shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative px-6 py-4 pr-14 border-b border-marine/15">
          <button
            type="button"
            onClick={onClose}
            disabled={!canClose}
            aria-label={backLabel ? `Back to ${backLabel}` : 'Close'}
            title={backLabel ? `Back to ${backLabel}` : 'Close'}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded text-ink-mid hover:bg-marine/10 hover:text-ink disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <X aria-hidden="true" strokeWidth={2.5} className="h-4 w-4" />
          </button>
          {backLabel && (
            <button type="button" onClick={onClose} className="mb-1 inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
              <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
              Back to {backLabel}
            </button>
          )}
          <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral">{eyebrow}</div>
          <h2 className="text-lg font-black text-ink">{title}</h2>
          {subtitle && <div className="text-[11px] text-ink-mid">{subtitle}</div>}
        </div>
        <div className="flex-1 overflow-auto">{children}</div>
        <div className="px-6 py-4 border-t border-marine/15">{footer}</div>
      </div>
    </div>
  )
}
